import React from 'react';
import PropTypes from 'prop-types';
import { CircularProgress } from '@mui/material';
import styled from '@emotion/styled';
import { theme } from '../../constants/theme';

const LoaderOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: ${theme.zIndex.modal};
  backdrop-filter: blur(2px);
`;

const LoaderContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.md};
`;

const LoaderText = styled.span`
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.text.secondary};
  font-weight: ${theme.typography.fontWeight.medium};
`;

const LoaderImage = styled.img`
  width: 64px;
  height: 64px;
  margin-bottom: ${theme.spacing.sm};
`;

/**
 * CustomLoader Component
 * 
 * A full-screen loading overlay with spinner and optional text.
 * 
 * @component
 * @example
 * ```jsx
 * <CustomLoader loading={isLoading} text="Loading data..." />
 * ```
 */
const CustomLoader = ({
  loading = false,
  text = '',
  size = 90,
  color = 'primary',
  showAppIcon = true,
}) => {
  if (!loading) return null;

  return (
    <LoaderOverlay>
      <LoaderContent>
        {showAppIcon && (
          <LoaderImage
            src="/images/AppIcon_64x64.png"
            alt="Loading"
          />
        )}
        <CircularProgress
          size={size}
          sx={{
            color: color === 'primary' ? theme.colors.primary.main : color,
          }}
        />
        {text && <LoaderText>{text}</LoaderText>}
      </LoaderContent>
    </LoaderOverlay>
  );
};

CustomLoader.propTypes = {
  /** Show/hide loader */
  loading: PropTypes.bool.isRequired,
  
  /** Loading text to display */
  text: PropTypes.string,
  
  /** Spinner size in pixels */
  size: PropTypes.number,
  
  /** Spinner color */
  color: PropTypes.string,
  
  /** Show app icon above spinner */
  showAppIcon: PropTypes.bool,
};

export default CustomLoader;
